import React from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import SettingsProjectSubNav from './SettingsProjectSubNav';
import Util from '../../shared/util/util';
import {ForecastThemeProvider} from '../../shared/ForecastThemeProvider';
import type {SettingsProjectStages_viewer$key} from './__generated__/SettingsProjectStages_viewer.graphql';

const Page = Styled.div`
	max-width: 1000px;
	margin-left: 24px;
	margin-top: 6px;
	font-size: 13px;
`;

const StageList = Styled.ol`
	list-style: none;
	padding: 0;
	margin: 0;
	max-width: 620px;
`;

const StageRow = Styled.li`
	padding: 10px 12px;
	border-bottom: 1px solid #e6e6e6;
`;

export type ProjectStageRow = {
	id: string;
	name: string | null;
	defaultStage: string | null;
	sortOrder: number | null;
};

export const SettingsProjectStagesComponent = ({stages}: {stages: ReadonlyArray<ProjectStageRow>}): JSX.Element => {
	const intl = useIntl();
	const ordered = [...stages].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

	// Built-in stages are translated from their default stage; any other stage shows the name
	// the Admin gave it.
	const label = (stage: ProjectStageRow) =>
		(stage.defaultStage && Util.getProjectStageTranslation(stage.defaultStage, intl)) || stage.name || '';

	return (
		<div className="section-content settings-app" data-cy={'settings-project-stages-page'}>
			<ForecastThemeProvider>
				<>
					<SettingsProjectSubNav />
					<Page>
						<StageList data-cy={'project-stages-list'}>
							{ordered.map(stage => (
								<StageRow key={stage.id} data-cy={'project-stage-row'}>
									{label(stage)}
								</StageRow>
							))}
						</StageList>
					</Page>
				</>
			</ForecastThemeProvider>
		</div>
	);
};

export const SettingsProjectStagesQuery = graphql`
	query SettingsProjectStagesQuery {
		viewer {
			actualPersonId
			component(name: "settings_project_stages")
			...SettingsProjectStages_viewer
		}
	}
`;

const VIEWER_FRAGMENT = graphql`
	fragment SettingsProjectStages_viewer on Viewer {
		company {
			id
			projectStages(first: 1000) {
				edges {
					node {
						id
						name
						defaultStage
						sortOrder
					}
				}
			}
		}
	}
`;

const SettingsProjectStages = ({viewer}: {viewer: SettingsProjectStages_viewer$key}): JSX.Element => {
	const data = useFragment(VIEWER_FRAGMENT, viewer);
	const stages = (data.company?.projectStages?.edges ?? []).flatMap(edge => (edge?.node ? [edge.node] : []));
	return <SettingsProjectStagesComponent stages={stages} />;
};

export default SettingsProjectStages;
